/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../integrations/supabase/client';
import type { AccessReasonCategory, PortalViewOptions, PortalAccessMetadata } from '@/types/portalAccess';

// Types
export interface ImpersonationSession {
  session_id: string;
  target_user_id: string;
  target_user_email: string;
  target_user_name: string;
  platform_tenant_id: string;
  tenant_name: string;
  access_level: 'read' | 'write';
  started_at: string;
}

/**
 * Client View Session
 * Used when tenant admin views a client's portal in read-only mode
 */
export interface ClientViewSession {
  session_id: string;
  admin_user_id: string;
  client_id: string;
  client_name: string;
  tenant_id: string;
  reason: string;
  reason_category: AccessReasonCategory;
  linked_ticket_id?: string;
  started_at: string;
  access_level: 'read'; // Always read-only for client view
}

export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: 'active' | 'banned' | 'unconfirmed';
  last_sign_in_at: string | null;
  created_at: string;
  active_sessions_count: number;
}

export interface UserSession {
  id: string;
  ip_address: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  location_country: string | null;
  location_city: string | null;
  created_at: string;
  last_active_at: string;
  is_current: boolean;
  is_revoked: boolean;
}

// Impersonation
export async function startImpersonation(
  targetUserId: string,
  reason: string,
  accessLevel: 'read' | 'write' = 'read'
): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase.rpc('start_impersonation', {
    p_admin_id: user.id,
    p_target_user_id: targetUserId,
    p_reason: reason,
    p_access_level: accessLevel
  });

  if (error) throw error;
  return data;
}

export async function endImpersonation(sessionId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('end_impersonation', {
    p_session_id: sessionId
  });

  if (error) throw error;
  return data;
}

export async function getActiveImpersonation(): Promise<ImpersonationSession | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase.rpc('get_active_impersonation', {
    p_admin_id: user.id
  });

  if (error) throw error;
  return (data?.[0] as unknown as ImpersonationSession) || null;
}

export async function getImpersonationHistory(
  limit: number = 50
): Promise<any[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('impersonation_sessions')
    .select(`
      *,
      target_user:profiles!impersonation_sessions_target_user_id_fkey(full_name, email),
      tenant:platform_tenants!impersonation_sessions_platform_tenant_id_fkey(company_name)
    `)
    .eq('admin_user_id', user.id)
    .order('started_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

// Tenant Users
export async function getTenantUsers(tenantId: string): Promise<TenantUser[]> {
  const { data, error } = await supabase.rpc('get_tenant_users', {
    p_tenant_id: tenantId
  });

  if (error) throw error;
  return (data || []) as unknown as TenantUser[];
}

// User Sessions
export async function getUserSessions(userId: string): Promise<UserSession[]> {
  const { data, error } = await supabase.rpc('get_user_sessions', {
    p_user_id: userId
  });

  if (error) throw error;
  return (data || []) as unknown as UserSession[];
}

export async function revokeSession(
  sessionId: string,
  reason?: string
): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase.rpc('revoke_user_session', {
    p_session_id: sessionId,
    p_revoked_by: user.id,
    p_reason: reason ?? undefined
  });

  if (error) throw error;
  return data;
}

export async function revokeAllUserSessions(
  userId: string,
  reason?: string
): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase.rpc('revoke_all_user_sessions', {
    p_user_id: userId,
    p_revoked_by: user.id,
    p_reason: reason ?? undefined
  });

  if (error) throw error;
  return data;
}

// Storage key for impersonation state
const IMPERSONATION_KEY = 'onboardit_impersonation';

export function storeImpersonationState(session: ImpersonationSession): void {
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));
}

export function getStoredImpersonationState(): ImpersonationSession | null {
  const stored = localStorage.getItem(IMPERSONATION_KEY);
  return stored ? JSON.parse(stored) : null;
}

export function clearImpersonationState(): void {
  localStorage.removeItem(IMPERSONATION_KEY);
}

export function isImpersonating(): boolean {
  return getStoredImpersonationState() !== null;
}

// ============================================
// Client View Functions (Tenant Admin → Client)
// ============================================

const CLIENT_VIEW_KEY = 'onboardit_client_view';

/**
 * Start a client portal view session
 * Validates admin has permission to view this client's portal
 */
export async function startClientPortalView(
  clientId: string,
  clientName: string,
  reason: string,
  options?: PortalViewOptions
): Promise<ClientViewSession> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get admin's profile to check tenant
  const { data: adminProfile, error: profileError } = await supabase
    .from('profiles')
    .select('platform_tenant_id')
    .eq('id', user.id)
    .single();

  if (profileError) throw new Error('Failed to get admin profile');

  // Get client's tenant to validate access
  const { data: client, error: clientError } = await supabase
    .from('clients')
    .select('platform_tenant_id, name')
    .eq('id', clientId)
    .single();

  if (clientError) throw new Error('Failed to get client information');

  // Check if admin is super_admin (can view any tenant)
  const { data: superAdminRole } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .eq('role', 'super_admin')
    .maybeSingle();

  const isSuperAdmin = !!superAdminRole;

  // Validate same tenant (unless super admin)
  if (!isSuperAdmin && adminProfile.platform_tenant_id !== client.platform_tenant_id) {
    throw new Error('You can only view clients within your own tenant');
  }

  const category = options?.category || 'other';

  // Create optional support ticket if requested
  let linkedTicketId: string | undefined;
  if (options?.createTicket) {
    // For now, we'll log the intent - Communications Hub integration would go here
    // In production, this would call CommunicationsHubService.createSupportThread()
    // TODO: Integrate with CommunicationsHubService.createSupportThread()
    // Generate a placeholder ticket ID for tracking
    linkedTicketId = `ticket_${crypto.randomUUID().slice(0, 8)}`;
  }

  // Create session with enhanced metadata
  const session: ClientViewSession = {
    session_id: crypto.randomUUID(),
    admin_user_id: user.id,
    client_id: clientId,
    client_name: clientName || client.name,
    tenant_id: client.platform_tenant_id,
    reason,
    reason_category: category,
    linked_ticket_id: linkedTicketId,
    started_at: new Date().toISOString(),
    access_level: 'read',
  };

  // Enhanced metadata for audit log
  const metadata: PortalAccessMetadata = {
    session_id: session.session_id,
    reason,
    reason_category: category,
    client_id: clientId,
    client_name: clientName || client.name,
    linked_ticket_id: linkedTicketId,
    session_context: {
      from_page: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    },
  };

  // Log the action for audit
  await supabase.from('audit_logs').insert([{
    action: 'client_portal_view_started',
    resource_type: 'client',
    resource_id: clientId,
    resource_name: clientName,
    user_id: user.id,
    description: `Admin started viewing client portal [${category}]: ${reason}`,
    metadata: metadata as any,
  }]);

  // Store session in localStorage
  storeClientViewState(session);

  return session;
}

/**
 * End a client portal view session
 */
export async function endClientPortalView(): Promise<void> {
  const session = getStoredClientViewSession();
  if (!session) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Calculate session duration
  const startTime = new Date(session.started_at).getTime();
  const endTime = Date.now();
  const durationSeconds = Math.floor((endTime - startTime) / 1000);

  // Enhanced metadata for analytics
  const metadata: PortalAccessMetadata = {
    session_id: session.session_id,
    reason: session.reason,
    reason_category: session.reason_category || 'other',
    client_id: session.client_id,
    client_name: session.client_name,
    linked_ticket_id: session.linked_ticket_id,
    duration_seconds: durationSeconds,
  };

  // Log the end of session
  await supabase.from('audit_logs').insert([{
    action: 'client_portal_view_ended',
    resource_type: 'client',
    resource_id: session.client_id,
    resource_name: session.client_name,
    user_id: user.id,
    description: `Admin ended client portal view [${session.reason_category || 'other'}] after ${durationSeconds}s`,
    metadata: metadata as any,
  }]);

  clearClientViewState();
}

export function storeClientViewState(session: ClientViewSession): void {
  localStorage.setItem(CLIENT_VIEW_KEY, JSON.stringify(session));
}

export function getStoredClientViewSession(): ClientViewSession | null {
  const stored = localStorage.getItem(CLIENT_VIEW_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function clearClientViewState(): void {
  localStorage.removeItem(CLIENT_VIEW_KEY);
}

export function isViewingClientPortal(): boolean {
  return getStoredClientViewSession() !== null;
}

/**
 * Get the client ID being viewed (if any)
 * Useful for overriding client context in queries
 */
export function getViewedClientId(): string | null {
  const session = getStoredClientViewSession();
  return session?.client_id || null;
}
